import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/api";
import { User, Lock, Save, CheckCircle, AlertCircle } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [profileForm, setProfileForm] = useState({ full_name: user?.full_name || "", avatar_url: user?.avatar_url || "" });
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [profileSaved, setProfileSaved] = useState(false);
  const [pwdSaved, setPwdSaved] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileError("");
    try {
      const r = await api.put("/profile", profileForm);
      setUser(r.data);
      localStorage.setItem("user", JSON.stringify(r.data));
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (err) {
      setProfileError(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePwdSave = async (e) => {
    e.preventDefault();
    setPwdError("");
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      setPwdError("Passwords do not match");
      return;
    }
    if (pwdForm.new_password.length < 8) {
      setPwdError("Password must be at least 8 characters");
      return;
    }
    setSavingPwd(true);
    try {
      await api.put("/profile/password", { current_password: pwdForm.current_password, new_password: pwdForm.new_password });
      setPwdSaved(true);
      setPwdForm({ current_password: "", new_password: "", confirm_password: "" });
      setTimeout(() => setPwdSaved(false), 3000);
    } catch (err) {
      setPwdError(err.response?.data?.detail || "Password change failed");
    } finally {
      setSavingPwd(false);
    }
  };

  const initials = user?.full_name?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";
  const roleLabel = user?.role === "superadmin" ? "Super Admin" : user?.role === "company_admin" ? "Company Admin" : "User";

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#E5F0F2] flex items-center justify-center text-[#006080] text-xl font-bold flex-shrink-0">
          {initials}
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">{user?.full_name}</h2>
          <p className="text-sm text-slate-500">{user?.email}</p>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#E5F0F2] text-[#006080] mt-1 inline-block">{roleLabel}</span>
        </div>
      </div>

      {/* Profile form */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-5">
          <User size={16} className="text-[#006080]" />
          <h3 className="text-sm font-semibold text-slate-900">Profile Information</h3>
        </div>
        {profileError && <div data-testid="profile-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14}/>{profileError}</div>}
        {profileSaved && <div data-testid="profile-saved" className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-700 flex items-center gap-2"><CheckCircle size={14}/>Profile updated successfully.</div>}
        <form onSubmit={handleProfileSave} className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Full Name</label>
            <input data-testid="profile-name-input" value={profileForm.full_name} onChange={e=>setProfileForm(f=>({...f,full_name:e.target.value}))} required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Email Address</label>
            <input value={user?.email || ""} disabled className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-slate-50 text-slate-400 cursor-not-allowed" />
            <p className="text-xs text-slate-400 mt-1">Email cannot be changed from here.</p>
          </div>
          <button data-testid="save-profile-button" type="submit" disabled={savingProfile} className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50">
            <Save size={13} />{savingProfile ? "Saving..." : "Save Profile"}
          </button>
        </form>
      </div>

      {/* Password form */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-5">
          <Lock size={16} className="text-[#006080]" />
          <h3 className="text-sm font-semibold text-slate-900">Change Password</h3>
        </div>
        {pwdError && <div data-testid="pwd-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14}/>{pwdError}</div>}
        {pwdSaved && <div data-testid="pwd-saved" className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-700 flex items-center gap-2"><CheckCircle size={14}/>Password changed successfully.</div>}
        <form onSubmit={handlePwdSave} className="space-y-4">
          {[
            { label: "Current Password", key: "current_password", testId: "current-password-input" },
            { label: "New Password", key: "new_password", testId: "new-password-input" },
            { label: "Confirm New Password", key: "confirm_password", testId: "confirm-password-input" },
          ].map(({ label, key, testId }) => (
            <div key={key}>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">{label}</label>
              <input data-testid={testId} type="password" value={pwdForm[key]} onChange={e=>setPwdForm(f=>({...f,[key]:e.target.value}))} required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
            </div>
          ))}
          <button data-testid="save-password-button" type="submit" disabled={savingPwd} className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50">
            <Lock size={13} />{savingPwd ? "Changing..." : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
